import { Mail, Phone, User, IdCard } from "lucide-react";
import { Shimmer, SettingsShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useThemeMode } from "@/theme/ThemeContext";
import { useAuth } from "@/context/AuthContext";

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-4 py-3">
      <div className="text-muted-foreground flex">{icon}</div>
      <div className="flex-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

export default function GeneralSettingsPage() {
  const { mode, toggleTheme } = useThemeMode();
  const { user, isLoading } = useAuth();

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">
          General Settings
        </h1>
        <p className="text-muted-foreground">
          Your profile and application preferences.
        </p>
      </div>

      <Shimmer loading={isLoading} fallback={<SettingsShimmer />}>
        <div className="flex flex-col gap-6">
          {/* Profile */}
          <Card>
            <CardContent className="p-5">
              <h3 className="text-base font-semibold mb-2">
                Profile
              </h3>
              <Separator className="mb-4" />

              {user ? (
                <div>
                  {/* Avatar + Name header */}
                  <div className="flex items-center gap-4 mb-5">
                    <Avatar className="h-16 w-16 text-2xl">
                      <AvatarImage src={user.dp || undefined} />
                      <AvatarFallback>
                        {user.fullname.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-lg font-bold">
                        {user.fullname}
                      </h3>
                      {user.username && (
                        <Badge variant="outline" className="mt-1">@{user.username}</Badge>
                      )}
                    </div>
                  </div>

                  <Separator className="mb-2" />

                  {/* Detail rows */}
                  <DetailRow icon={<Mail className="h-5 w-5" />} label="Email" value={user.email} />
                  <Separator />
                  <DetailRow icon={<Phone className="h-5 w-5" />} label="Phone" value={user.phone} />
                  {user.username && (
                    <>
                      <Separator />
                      <DetailRow icon={<User className="h-5 w-5" />} label="Username" value={user.username} />
                    </>
                  )}
                  <Separator />
                  <DetailRow
                    icon={<IdCard className="h-5 w-5" />}
                    label="User ID"
                    value={user.id}
                  />
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">
                  Unable to load profile data.
                </span>
              )}
            </CardContent>
          </Card>

        {/* Appearance */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-base font-semibold mb-2">
              Appearance
            </h3>
            <Separator className="mb-4" />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  Dark Mode
                </p>
                <span className="text-sm text-muted-foreground">
                  Switch between light and dark themes
                </span>
              </div>
              <Switch checked={mode === "dark"} onCheckedChange={toggleTheme} />
            </div>
          </CardContent>
        </Card>
        </div>
      </Shimmer>
    </div>
  );
}
